import React, { useState, useEffect } from 'react';
import { useStomp } from '../../contexts/StompContext';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import ugovLogo from '../../assets/logo.png';
import './Messaging.css';

const MessagingApp = ({ user, onClose, onHome, targetConversation }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { addMessageHandler, removeMessageHandler } = useStomp();

  // Helper function to safely get conversation ID
  const getConversationId = (conv) => {
    if (!conv) return null;
    
    const possibleIds = [
      conv._id?.$oid,
      conv._id,
      conv.id?.$oid,
      conv.id,
      conv['_id']?.$oid,
      conv['_id'],
      conv['id']
    ];
    
    return possibleIds.find(id => id && id !== 'undefined' && typeof id === 'string');
  };

  useEffect(() => {
    fetchConversations();
    
    // Subscribe to user's chat messages
    const handleChatMessage = (message, topic) => {
      console.log('STOMP: Received chat message', message);
      
      if (message.message) {
        handleNewMessage(message);
      }
    };

    const cleanup = addMessageHandler('chat', handleChatMessage);

    return cleanup;
  }, [user.stomp_key, addMessageHandler]);

  // Handle target conversation when provided
  useEffect(() => {
    if (targetConversation) {
      console.log('MessagingApp: Setting target conversation:', targetConversation);
      
      // Add the target conversation to conversations if it's not already there
      setConversations(prev => {
        const existingConv = prev.find(conv => 
          getConversationId(conv) === getConversationId(targetConversation)
        );
        
        if (!existingConv) {
          return [targetConversation, ...prev];
        }
        return prev;
      });
      
      // Select the target conversation and mark as read
      handleConversationSelect(targetConversation);
    }
  }, [targetConversation]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/conversations?include_latest_message=true&per_page=50');
      const data = await response.json();
      
      if (data.conversations) {
        setConversations(data.conversations);
        
        // Auto-select first conversation if none selected
        if (!selectedConversation && data.conversations.length > 0) {
          setSelectedConversation(data.conversations[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (messageData) => {
    const { message, conversation, notification } = messageData;
    
    // Update conversations list with new message
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (getConversationId(conv) === getConversationId(conversation)) {
          return {
            ...conv,
            latest_message: message,
            has_unread: getConversationId(conv) !== getConversationId(selectedConversation),
            updated_at: message.created_at
          };
        }
        return conv;
      });
    });

    // If this message is for the currently selected conversation, 
    // the ChatWindow will handle it via its own STOMP subscription
  };

  const handleConversationSelect = async (conversation) => {
    setSelectedConversation(conversation);
    
    // Mark conversation as read locally
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (getConversationId(conv) === getConversationId(conversation)) {
          return { ...conv, has_unread: false };
        }
        return conv;
      });
    });

    // Note: Messages will be marked as read automatically when loadMessages is called in ChatWindow
    // The messages API automatically destroys notifications and returns updated unread_message_count
  };

  const handleNewConversation = async (usernames, title = '') => {
    try {
      const formData = new FormData();
      usernames.forEach(username => {
        formData.append('conversation[neighbors][]', username);
      });
      if (title) {
        formData.append('conversation[title]', title);
      }

      const response = await fetch('/api/v1/conversations', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.conversations || data.conversation) {
        const newConversation = data.conversations || data.conversation;
        setConversations(prev => [newConversation, ...prev]);
        setSelectedConversation(newConversation);
        return newConversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in conversation title
    if (conv.title && conv.title.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in user names
    if (conv.users) {
      return conv.users.some(user => 
        user.username && user.username.toLowerCase().includes(searchLower)
      );
    }
    
    // Search in latest message
    if (conv.latest_message && conv.latest_message.body) {
      return conv.latest_message.body.toLowerCase().includes(searchLower);
    }
    
    return false;
  });

  return (
    <div className="messaging-app">
      <div className="messaging-header">
        <div className="messaging-header-content">
          <div className="messaging-logo-section" onClick={onHome} style={{cursor: 'pointer'}}>
            <img src={ugovLogo} alt="ugov" className="messaging-logo" />
            <h2>Conversas</h2>
          </div>
        </div>
      </div>
      
      <div className="messaging-content">
        <div className="conversations-panel">
          <div className="search-container">
            <input
              type="text"
              placeholder="Pesquisar conversas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <ConversationList
            conversations={filteredConversations}
            selectedConversation={selectedConversation}
            onConversationSelect={handleConversationSelect}
            onNewConversation={handleNewConversation}
            currentUser={user}
            loading={loading}
          />
        </div>
        
        <div className="chat-panel">
          {selectedConversation ? (
            <ChatWindow
              conversation={selectedConversation}
              currentUser={user}
              onConversationUpdate={(updatedConv) => {
                setConversations(prev => 
                  prev.map(conv => 
                    getConversationId(conv) === getConversationId(updatedConv) ? updatedConv : conv
                  )
                );
                setSelectedConversation(updatedConv);
              }}
            />
          ) : (
            <div className="no-conversation-selected">
              <p>Selecione uma conversa para começar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagingApp;
